import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Check, ChevronsUpDown, Search, X } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Colour map for the compact per-day cells. Keys are the short codes the
// backend actually stores (P, WO, AL, OH, RH, AHD, UPHD, UP, LWP) — the
// long-name variants (``present``, ``week_off``, …) were a mistake and left
// every cell falling through to the default '·' render. Mirrors
// AttendancePage.js statusColors so a manager glancing at both views sees
// consistent colours.
const STATUS_COLORS = {
  P: 'bg-green-500 text-white',           // Present
  UP: 'bg-red-500 text-white',            // Unpaid
  AL: 'bg-blue-500 text-white',           // Annual Leave (as attendance)
  AHD: 'bg-green-300 text-green-900',     // Approved Half Day
  UPHD: 'bg-red-300 text-red-900',        // Unpaid Half Day
  OH: 'bg-orange-500 text-white',         // Organization Holiday
  RH: 'bg-purple-500 text-white',         // Restricted Holiday
  WO: 'bg-gray-400 text-white',           // Week Off
  LWP: 'bg-red-600 text-white',           // Leave Without Pay
};

const STATUS_SHORT = {
  P: 'P', UP: 'UP', AL: 'AL', AHD: 'AH', UPHD: 'UH',
  OH: 'OH', RH: 'RH', WO: 'WO', LWP: 'LWP',
};

const LEAVE_COLORS = {
  AL: 'bg-blue-500 text-white',
  OH: 'bg-orange-500 text-white',
  RH: 'bg-purple-500 text-white',
  LWP: 'bg-red-600 text-white',
  CL: 'bg-teal-500 text-white',
  SL: 'bg-yellow-500 text-black',
};

/**
 * Build a lookup of ``{ 'YYYY-MM-DD': cell }`` for one employee so the
 * per-day grid can render in a single pass.
 *
 * Precedence: attendance FIRST, leaves as an overlay only where no attendance
 * row exists. This is important because once a leave is approved the
 * ``attendance`` collection stores the resolved status code (e.g. ``AHD`` for
 * an approved half-day, ``UPHD`` for an unpaid half-day, ``AL`` for a full-day
 * annual leave) — that record is the ledger of truth. Overlaying the raw
 * ``leaves.leave_type`` on top would flatten a half-day back to "AL" in the
 * team view even though the employee's own calendar (which uses attendance)
 * correctly shows "AH". So we treat the leaves list only as a fallback for
 * dates that have no attendance row yet (pending leaves).
 */
const buildDayIndex = (employee, year, month, daysInMonth) => {
  const dayMap = {};
  const pad = (n) => String(n).padStart(2, '0');

  (employee.attendance || []).forEach((a) => {
    if (!a?.date) return;
    dayMap[a.date] = {
      kind: 'attendance',
      status: a.status,
      approval_status: a.approval_status,
    };
  });

  (employee.leaves || []).forEach((lv) => {
    if (!lv?.from_date || !lv?.to_date) return;
    // Overlay every date in the leave range (bounded to this month), but ONLY
    // when we don't already have an attendance row for that date — that row
    // is authoritative.
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${year}-${pad(month)}-${pad(d)}`;
      if (iso >= lv.from_date && iso <= lv.to_date && !dayMap[iso]) {
        dayMap[iso] = {
          kind: 'leave',
          leave_type: lv.leave_type,
          leave_category: lv.leave_category,
          approval_status: lv.approval_status,
        };
      }
    }
  });

  return dayMap;
};

/**
 * TeamAttendanceView — Rendered as a tab inside the Attendance page for
 * users who manage at least one direct report (or for admins/superadmins).
 *
 * UX: search-filterable multi-select of team members + month/year pickers +
 * Submit. On submit, hits ``GET /api/team/attendance`` and renders a
 * compact day-grid: rows = employees, columns = days 1..N of the month,
 * cells colour-coded by attendance/leave status.
 */
const TeamAttendanceView = ({ user }) => {
  const today = new Date();
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const fetchMembers = async () => {
      setMembersLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/team/members`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMembers(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load team');
      } finally {
        setMembersLoading(false);
      }
    };
    fetchMembers();
  }, [user?.id]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter(m =>
      m.name?.toLowerCase().includes(q) ||
      m.employee_id?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q) ||
      m.department?.toLowerCase().includes(q)
    );
  }, [members, search]);

  const yearOptions = useMemo(() => {
    const y = today.getFullYear();
    return [y - 1, y, y + 1];
  }, [today]);

  const toggleId = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllFiltered = () => {
    const ids = filteredMembers.map(m => m.id);
    setSelectedIds(prev => Array.from(new Set([...prev, ...ids])));
  };

  const clearSelection = () => setSelectedIds([]);

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one team member');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/team/attendance`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { month: selectedMonth, year: selectedYear, employee_ids: selectedIds.join(',') },
      });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch team attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedMembers = useMemo(
    () => members.filter(m => selectedIds.includes(m.id)),
    [members, selectedIds],
  );

  return (
    <div className="space-y-6" data-testid="team-attendance-view">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-600" />
            <CardTitle>View Team Attendance</CardTitle>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Select team members and a month, then click Submit to see their attendance side-by-side.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Team member multi-select with dynamic search */}
          <div>
            <Label>Team Members ({selectedIds.length} selected of {members.length})</Label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between font-normal"
                  data-testid="team-picker-trigger"
                >
                  <span className="truncate">
                    {selectedIds.length === 0
                      ? 'Select team members…'
                      : `${selectedIds.length} member${selectedIds.length > 1 ? 's' : ''} selected`}
                  </span>
                  <ChevronsUpDown className="w-4 h-4 opacity-60 flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-h-[420px] overflow-hidden" align="start">
                <div className="flex items-center border-b px-3">
                  <Search className="w-4 h-4 opacity-60 mr-2 flex-shrink-0" />
                  <input
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, ID, email, department…"
                    className="flex-1 py-2 outline-none text-sm bg-transparent"
                    data-testid="team-picker-search"
                  />
                </div>
                <div className="flex items-center justify-between px-3 py-1.5 border-b text-xs bg-gray-50">
                  <span className="text-gray-500">
                    Showing {filteredMembers.length} of {members.length}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllFiltered}
                      className="text-blue-600 hover:underline"
                      data-testid="team-picker-select-all"
                    >
                      Select all filtered
                    </button>
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="text-red-600 hover:underline"
                      data-testid="team-picker-clear"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[300px]">
                  {membersLoading ? (
                    <div className="p-4 text-sm text-gray-500">Loading…</div>
                  ) : filteredMembers.length === 0 ? (
                    <div className="p-4 text-sm text-gray-500" data-testid="team-picker-empty">
                      No matches.
                    </div>
                  ) : (
                    filteredMembers.map((m) => {
                      const on = selectedIds.includes(m.id);
                      return (
                        <button
                          type="button"
                          key={m.id}
                          onClick={() => toggleId(m.id)}
                          className={`w-full flex items-start gap-3 px-3 py-2 hover:bg-gray-50 border-b last:border-b-0 text-left ${on ? 'bg-emerald-50' : ''}`}
                          data-testid={`team-picker-option-${m.employee_id || m.id}`}
                        >
                          <div className={`w-4 h-4 rounded border mt-0.5 flex-shrink-0 flex items-center justify-center ${on ? 'bg-emerald-600 border-emerald-600' : 'bg-white border-gray-300'}`}>
                            {on && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {m.name} <span className="text-xs text-gray-500 font-normal">· {m.employee_id}</span>
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {m.department || 'No dept'} · {m.designation || m.email}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Selected chips */}
            {selectedMembers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="team-picker-chips">
                {selectedMembers.map((m) => (
                  <span
                    key={m.id}
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 text-xs px-2 py-0.5"
                    data-testid={`team-picker-chip-${m.employee_id || m.id}`}
                  >
                    {m.name} · {m.employee_id}
                    <button
                      type="button"
                      onClick={() => toggleId(m.id)}
                      className="hover:text-emerald-950"
                      aria-label={`Remove ${m.name}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Month + Year + Submit */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div>
              <Label>Month</Label>
              <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                <SelectTrigger data-testid="team-month-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, idx) => (
                    <SelectItem key={idx + 1} value={String(idx + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Year</Label>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                <SelectTrigger data-testid="team-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(y => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={submitting || selectedIds.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 h-10"
              data-testid="team-submit-btn"
            >
              {submitting ? 'Loading…' : 'Submit'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && <TeamAttendanceGrid result={result} />}
    </div>
  );
};

const TeamAttendanceGrid = ({ result }) => {
  const { month, year, days_in_month: daysInMonth, employees } = result;

  if (!employees || employees.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500" data-testid="team-grid-empty">
          No attendance data available for the selected employees and month.
        </CardContent>
      </Card>
    );
  }

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {MONTHS[month - 1]} {year} · {employees.length} employee{employees.length > 1 ? 's' : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto" data-testid="team-attendance-grid">
          <table className="w-full text-xs border-collapse">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 border-b sticky left-0 bg-gray-50 z-10 min-w-[180px]">Employee</th>
                {days.map(d => (
                  <th key={d} className="px-1 py-2 border-b border-l text-center font-medium text-gray-700 w-7">
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => {
                const idx = buildDayIndex(emp, year, month, daysInMonth);
                return (
                  <tr key={emp.id} data-testid={`team-row-${emp.employee_id || emp.id}`}>
                    <td className="px-3 py-2 border-b sticky left-0 bg-white z-10">
                      <div className="font-medium text-gray-900 text-sm">{emp.name}</div>
                      <div className="text-xs text-gray-500">{emp.employee_id} · {emp.department || '—'}</div>
                    </td>
                    {days.map((d) => {
                      const pad = (n) => String(n).padStart(2, '0');
                      const iso = `${year}-${pad(month)}-${pad(d)}`;
                      const cell = idx[iso];
                      let cls = 'bg-white text-gray-300';
                      let label = '';
                      if (cell?.kind === 'leave') {
                        // Pending / not-yet-approved leaves — no attendance row
                        // has been written for them yet. Reflect half-day
                        // leaves as "AH" so the cell reads the same as the
                        // employee's own calendar once the leave is approved.
                        if (cell.leave_category === 'half_day') {
                          cls = STATUS_COLORS.AHD;
                          label = STATUS_SHORT.AHD;
                        } else {
                          cls = LEAVE_COLORS[cell.leave_type] || 'bg-gray-300 text-white';
                          label = cell.leave_type || 'L';
                        }
                      } else if (cell?.kind === 'attendance') {
                        cls = STATUS_COLORS[cell.status] || 'bg-gray-200 text-gray-700';
                        label = STATUS_SHORT[cell.status] || cell.status || '·';
                      } else {
                        label = '·';
                      }
                      return (
                        <td
                          key={d}
                          className="px-0 py-0 border-b border-l text-center"
                          data-testid={`team-cell-${emp.employee_id || emp.id}-${d}`}
                          title={cell ? `${iso} — ${cell.leave_type || cell.status}${cell.approval_status ? ` (${cell.approval_status})` : ''}` : iso}
                        >
                          <div className={`w-6 h-6 mx-auto text-[10px] leading-6 font-medium ${cls}`}>
                            {label}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Compact legend — matches AttendancePage.statusColors */}
        <div className="px-4 py-3 border-t flex flex-wrap gap-2 text-xs items-center bg-gray-50">
          <span className="text-gray-600 font-medium mr-1">Legend:</span>
          <LegendChip cls="bg-green-500 text-white" text="P Present" />
          <LegendChip cls="bg-red-500 text-white" text="UP Unpaid" />
          <LegendChip cls="bg-green-300 text-green-900" text="AH Approved Half-day" />
          <LegendChip cls="bg-red-300 text-red-900" text="UH Unpaid Half-day" />
          <LegendChip cls="bg-gray-400 text-white" text="WO Week-off" />
          <LegendChip cls="bg-blue-500 text-white" text="AL Annual Leave" />
          <LegendChip cls="bg-orange-500 text-white" text="OH Org Holiday" />
          <LegendChip cls="bg-purple-500 text-white" text="RH Restricted Holiday" />
          <LegendChip cls="bg-red-600 text-white" text="LWP Leave w/o Pay" />
        </div>
      </CardContent>
    </Card>
  );
};

const LegendChip = ({ cls, text }) => (
  <span className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 ${cls}`}>{text}</span>
);

export default TeamAttendanceView;
